import { TerminalEventMonitoring } from '@lightspeed-payments/payments-terminal-sdk'
import { Dispatch, useEffect } from 'react'
import VendNumber from 'vend-number'
import { TerminalInfo } from '../../../../helpers/lspay.types'
import {
  LSPAY_ERROR,
  SWIPER_ERROR_CODES,
  TRANSACTION_TYPE,
} from '../../../../helpers/lspayConstants'
import { createRefund, getRefundError } from '../../../../helpers/refund'
import { sendTerminalEvent } from '../../../../helpers/terminalMonitoringEventDispatcher'
import { TAG, trackLSPayServerError } from '../../../../hooks/errorTracking'
import { RefundEvent } from './use-lspay-refund-server-reducer/types'
import { useDispatchRefundEvent } from './useDispatchRefundEvent'

interface createRefundParams {
  amount: VendNumber
  currency: string
  originalRegisterSalePaymentIds?: string[]
  registerId: string
  registerSaleId: string
  retailerPaymentTypeId: string
  registerSalePaymentId: string
  consentCollected: boolean
  dispatch: Dispatch<RefundEvent>
  terminalMonitoringInstance: TerminalEventMonitoring | null
  terminalInfo: TerminalInfo
}

export const useCreateRefund = ({
  amount,
  currency,
  originalRegisterSalePaymentIds,
  registerId,
  registerSaleId,
  retailerPaymentTypeId,
  registerSalePaymentId,
  consentCollected,
  dispatch,
  terminalMonitoringInstance,
  terminalInfo,
}: createRefundParams) => {
  const { dispatchEvent, getErrorMessage } = useDispatchRefundEvent()
  useEffect(() => {
    const transaction = async () => {
      try {
        const response = await createRefund({
          amount,
          currency,
          originalRegisterSalePaymentIds,
          registerId,
          retailerPaymentTypeId,
          registerSalePaymentId,
          consentCollected,
          metadata: {
            register_sale_id: registerSaleId,
          },
        })
        const { setup_intent_id: setupIntentId } = response

        dispatchEvent({
          dispatch,
          setupIntentId,
          terminalInfo,
          data: response,
          terminalMonitoringInstance,
          // Skip the complete event with a "completed" status if consent is not collected,
          // meaning the refund is not card-presented.
          // Only non-Interac referenced refunds should receive a COMPLETE refund status
          // from the creatingRefund state, where consent is false.
          skipCompletedEvent: !consentCollected,
        })
      } catch (error: any) {
        // Send terminal monitoring event
        if (consentCollected) {
          // Send terminal event only if consent is collected.
          // A collected consent implies a card-presented refund, which involves the terminal.
          const { deviceId, deviceModel } = terminalInfo
          consentCollected &&
            sendTerminalEvent({
              terminalMonitoringInstance,
              terminal: {
                id: deviceId,
                model: deviceModel,
              },
              reason: error.message || 'Unable to create refund',
              transactionType: TRANSACTION_TYPE.REFUND,
              swiperError: error,
            })
        }

        trackLSPayServerError(
          '[lspay-server-driven] failed to create refund',
          error,
          {
            registerSalePaymentId,
            registerSaleId,
          },
          TAG.refund
        )
        const isPairingNotFound =
          error?.code === SWIPER_ERROR_CODES.CODE_REGISTER_PAIRING_NOT_FOUND
        const lspayError: LSPAY_ERROR = isPairingNotFound
          ? LSPAY_ERROR.PAIRING_MISSING_REFUND
          : getRefundError(error?.code)
        const disableRetry =
          lspayError === LSPAY_ERROR.PAIRING_MISSING_REFUND ||
          lspayError === LSPAY_ERROR.REFUND_IN_PROGRESS ||
          lspayError === LSPAY_ERROR.REFUND_EXCEEDS_SALE_BALANCE ||
          lspayError === LSPAY_ERROR.UNREF_REFUND_NOT_ENABLED

        dispatch({
          type: 'ERROR',
          error,
          errorMessage: getErrorMessage(lspayError),
          disableRetry,
        })
      }
    }
    transaction()
  }, [
    amount,
    consentCollected,
    currency,
    terminalInfo,
    dispatch,
    registerId,
    registerSaleId,
    registerSalePaymentId,
    retailerPaymentTypeId,
    terminalMonitoringInstance,
    dispatchEvent,
    getErrorMessage,
    originalRegisterSalePaymentIds,
  ])
}
